import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { ITicketAutomation } from "../../models/ticket-automation-rules";

export interface IRuleTemplate {
  name: string;
  actionTypeCode: string | null;
}

interface IAutomationRulesContextProps {
  automation: ITicketAutomation | null;
  selectAutomation: (value: ITicketAutomation | null) => void;
  ruleTemplate: IRuleTemplate | null;
  selectRuleTemplate: (value: IRuleTemplate | null) => void;
}

const AutomationRulesContext = createContext<IAutomationRulesContextProps>({
  automation: null,
  selectAutomation: () => {},
  ruleTemplate: null,
  selectRuleTemplate: () => {},
});

interface IAutomationRulesProviderProps {
  children: JSX.Element;
}

export const AutomationRulesProvider: React.FC<IAutomationRulesProviderProps> =
  memo(({ children }) => {
    const [automation, setAutomation] = useState<ITicketAutomation | null>(
      null
    );

    const [ruleTemplate, setRuleTemplate] = useState<IRuleTemplate | null>(
      null
    );

    const selectAutomation = useCallback((value: ITicketAutomation | null) => {
      setAutomation(value);
    }, []);

    const selectRuleTemplate = useCallback((value: IRuleTemplate | null) => {
      setRuleTemplate(value);
    }, []);

    const value = useMemo(
      () => ({
        automation,
        selectAutomation,
        ruleTemplate,
        selectRuleTemplate,
      }),
      [
        automation,
        selectAutomation,
        ruleTemplate,
        selectRuleTemplate,
      ]
    );

    return (
      <AutomationRulesContext.Provider value={value}>
        {children}
      </AutomationRulesContext.Provider>
    );
  });

export const useAutomationRules = () => {
  const context = useContext(AutomationRulesContext);

  if (!context) {
    throw new Error("useAutomationRules must be used with DashBoardProvider");
  }

  return context;
};
